/**
 * Skio line item properties bridge.
 *
 * Our subscribe-interval-cards UI replaces Skio's stock subscription widget,
 * so the line item properties Skio normally injects on /cart/add.js aren't
 * sent. Rebuy's Skio integration uses these properties (in addition to the
 * Shopify `selling_plan` field) to display subscription labels and the
 * "Upgrade to subscription" widget on the smart cart.
 *
 * This script watches for subscribe-card radio changes (and one-time link
 * resets) and copies the selected card's data attributes into the hidden
 * form inputs rendered by snippets/subscribe-interval-cards.liquid:
 *
 *   data-skio-prop="group"     →  name="properties[_skio_selling_plan_group]"
 *   data-skio-prop="freq"      →  name="properties[shipping_interval_frequency]"
 *   data-skio-prop="unit"      →  name="properties[shipping_interval_unit_type]"
 *   data-skio-prop="discount"  →  name="properties[Discount]"
 *
 * The `Discount` property is what Rebuy's Smart Cart reads to render a
 * visible "Discount : 25%" label on the line item (mirrors Skio's own
 * plan-picker widget behaviour).
 *
 * When no subscription is selected (one-time, or all cards disabled), the
 * `name` attribute is removed so the inputs don't submit empty properties.
 */
(function () {
  var PROP_NAME = {
    group: 'properties[_skio_selling_plan_group]',
    freq: 'properties[shipping_interval_frequency]',
    unit: 'properties[shipping_interval_unit_type]',
    discount: 'properties[Discount]',
  };

  function getFormId(formOrEl) {
    if (!formOrEl) return '';
    if (typeof formOrEl.getAttribute === 'function') {
      return formOrEl.getAttribute('id') || formOrEl.getAttribute('form') || '';
    }
    return '';
  }

  function findHiddenInputs(formId) {
    if (!formId) return null;
    // Hidden inputs have form="<formId>" so they submit with the product form
    // even though they live outside it (inside <variant-picker>).
    var sel = function (prop) {
      return document.querySelector(
        'input[data-skio-prop="' + prop + '"][form="' + CSS.escape(formId) + '"]'
      );
    };
    var inputs = {
      group: sel('group'),
      freq: sel('freq'),
      unit: sel('unit'),
      discount: sel('discount'),
    };
    if (!inputs.group && !inputs.freq && !inputs.unit && !inputs.discount) return null;
    return inputs;
  }

  function clearInputs(inputs) {
    Object.keys(inputs).forEach(function (k) {
      var inp = inputs[k];
      if (!inp) return;
      inp.removeAttribute('name');
      inp.value = '';
    });
  }

  function applyFromRadio(inputs, radio) {
    if (!radio || radio.disabled) {
      clearInputs(inputs);
      return;
    }
    var values = {
      group: radio.getAttribute('data-skio-group') || '',
      freq: radio.getAttribute('data-skio-freq') || '',
      unit: radio.getAttribute('data-skio-unit') || '',
      discount: radio.getAttribute('data-skio-discount') || '',
    };
    // We need at least the group/freq/unit triplet to declare this a Skio
    // subscription. If any of those is missing, treat as not-a-subscription
    // and don't submit partial properties. Discount is optional — if the
    // selling plan has no percentage adjustment, we just skip that one.
    if (!values.group || !values.freq || !values.unit) {
      clearInputs(inputs);
      return;
    }
    Object.keys(values).forEach(function (k) {
      var inp = inputs[k];
      if (!inp) return;
      var v = values[k];
      if (!v) {
        // Empty value (e.g. no discount on this plan) — skip submitting
        // this specific property but keep the rest active.
        inp.removeAttribute('name');
        inp.value = '';
        return;
      }
      inp.value = v;
      inp.name = PROP_NAME[k];
    });
  }

  function updateForFormId(formId) {
    var inputs = findHiddenInputs(formId);
    if (!inputs) return;
    // Find the checked card radio within the form's subscribe-interval-cards.
    var radio = document.querySelector(
      '.subscribe-interval-cards input[type="radio"][form="' +
        CSS.escape(formId) +
        '"]:checked:not(:disabled)'
    );
    applyFromRadio(inputs, radio);
  }

  // Reactive: when a card radio changes, recompute Skio properties.
  document.addEventListener('change', function (event) {
    var t = event.target;
    if (!t || t.tagName !== 'INPUT' || t.type !== 'radio') return;
    if (!t.closest('.subscribe-interval-cards')) return;
    var formId = t.getAttribute('form');
    if (!formId) return;
    updateForFormId(formId);
  });

  // When the variant/format changes, custom-product.js refreshes each card's
  // data-skio-* attributes (without firing a `change` event on the radio,
  // since the same interval value usually stays checked). It then dispatches
  // `subscribe-interval-cards:sync` on the form once the refresh is done.
  // We listen for that so the hidden inputs pick up the new plan's Skio data.
  document.addEventListener('subscribe-interval-cards:sync', function (event) {
    var form = event.target;
    if (!form || form.tagName !== 'FORM') return;
    var formId = form.getAttribute('id');
    if (!formId) return;
    updateForFormId(formId);
  });

  // One-time purchase link: when clicked, the form's selling_plan is cleared
  // by product-subscriptions.js. Mirror that here so Skio properties also clear.
  document.addEventListener('click', function (event) {
    var t = event.target;
    if (!t || !t.closest) return;
    var oneTimeBtn = t.closest('[data-one-time-purchase-link]');
    if (!oneTimeBtn) return;
    var formId = oneTimeBtn.getAttribute('form') || '';
    if (!formId) {
      // Fall back: find the closest form
      var form = oneTimeBtn.closest('form');
      if (form) formId = form.getAttribute('id') || '';
    }
    if (!formId) return;
    var inputs = findHiddenInputs(formId);
    if (inputs) clearInputs(inputs);
  });

  // Initial pass on load: if a card is already checked (default selection),
  // populate the hidden inputs.
  function initAll() {
    document
      .querySelectorAll('.subscribe-interval-cards input[type="radio"][form]:checked')
      .forEach(function (radio) {
        var formId = radio.getAttribute('form');
        if (formId) updateForFormId(formId);
      });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', initAll);
  } else {
    initAll();
  }
})();
